import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Button,
  Chip,
  Divider,
  Stack,
  Avatar,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DealIcon from '@mui/icons-material/Handshake';
import InventoryIcon from '@mui/icons-material/Inventory';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import SystemIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import ReadIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useDashboardNotifications } from '../../context/DashboardNotificationContext';

const NotificationsPage: React.FC = () => {
  const { notifications, markAsRead, markAllAsRead, unreadCount } = useDashboardNotifications();
  const navigate = useNavigate();

  const getIcon = (type: string) => {
    switch (type) {
      case 'deal': return <DealIcon color="primary" />;
      case 'inventory': return <InventoryIcon color="error" />;
      case 'wealth': return <MoneyIcon color="success" />;
      default: return <SystemIcon color="action" />;
    }
  };

  const getBgColor = (type: string) => {
    switch (type) {
      case 'deal': return 'primary.50';
      case 'inventory': return 'error.50';
      case 'wealth': return 'success.50';
      default: return 'grey.50';
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={4}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight="bold">Notifications</Typography>
          <Typography variant="body2" color="text.secondary">
            You have {unreadCount} unread alerts.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={markAllAsRead} disabled={unreadCount === 0}>
          Mark all as read
        </Button>
      </Stack>

      <Paper sx={{ borderRadius: '16px', overflow: 'hidden' }}>
        <List sx={{ p: 0 }}>
          {notifications.length === 0 ? (
            <Box sx={{ py: 10, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">All caught up!</Typography>
              <Typography variant="body2" color="text.secondary">No notifications yet.</Typography>
            </Box>
          ) : (
            notifications.map((notif, index) => (
              <React.Fragment key={notif.id}>
                <ListItem
                  sx={{
                    py: 3,
                    px: 4,
                    bgcolor: notif.read ? 'transparent' : 'action.hover',
                    transition: 'background-color 0.2s',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  secondaryAction={
                    <Stack direction="row" spacing={1}>
                      {!notif.read && (
                        <IconButton size="small" onClick={() => markAsRead(notif.id)} title="Mark as read">
                          <ReadIcon color="primary" fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton size="small" color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  }
                >
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: getBgColor(notif.type), color: 'inherit' }}>
                      {getIcon(notif.type)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography fontWeight={notif.read ? 500 : 700}>{notif.title}</Typography>
                        {!notif.read && <Chip label="New" size="small" color="primary" sx={{ height: 18, fontSize: '10px' }} />}
                      </Stack>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>{notif.message}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(notif.created_at).toLocaleString()}
                        </Typography>
                        {notif.link && (
                          <Button 
                            size="small" 
                            sx={{ mt: 1, display: 'block', textTransform: 'none' }} 
                            onClick={() => navigate(notif.link!)}
                          >
                            Take Action
                          </Button>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))
          )}
        </List>
      </Paper>
    </Box>
  );
};

export default NotificationsPage;
